import React, { useState, useEffect } from "react";
import {
  FaCalendarAlt, FaClock, FaMicrophone, FaCheckCircle, FaUser, FaBuilding
} from "react-icons/fa";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PodcastBook = () => {
  const [isLoading, setIsLoading] = useState(false);

  // Simple booking form data
  const [bookingData, setBookingData] = useState({
    // Basic Booking Details
    date: "",
    startTime: "",
    endTime: "",
    duration: "",

    // Podcast Information
    topic: "",
    podcastTitle: "",
    podcastDescription: "",

    // Guest Information
    guestName: "",
    guestEmail: "",
    guestPhone: "",

    // Contact Information
    contactPerson: "",
    contactPhone: "",
    contactEmail: "",
    companyName: "",

    // Additional Details
    notes: ""
  });

  const handleBookingSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    const requiredFields = ['date', 'startTime', 'endTime', 'topic', 'podcastTitle', 'contactPerson', 'contactPhone', 'contactEmail'];
    const missingFields = requiredFields.filter(field => !bookingData[field]);

    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    try {
      setIsLoading(true);

      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error("Please login to submit booking request");
        return;
      }

      // Prepare booking data for submission
      const bookingPayload = {
        ...bookingData
      };

      console.log('Submitting Podcast Booking Data:', bookingPayload);

      // Call real API
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/podcast/booking/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bookingPayload)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success("Podcast session booking submitted successfully! We will contact you soon.");
        resetBookingData();
      } else {
        toast.error(data.message || "Failed to submit booking. Please try again.");
      }

    } catch (error) {
      console.error('Booking submission error:', error);
      toast.error("Failed to submit booking. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const resetBookingData = () => {
    setBookingData({
      date: "",
      startTime: "",
      endTime: "",
      duration: "",
      topic: "",
      podcastTitle: "",
      podcastDescription: "",
      guestName: "",
      guestEmail: "",
      guestPhone: "",
      contactPerson: "",
      contactPhone: "",
      contactEmail: "",
      companyName: "",
      notes: ""
    });
  };

  const calculateDuration = () => {
    if (bookingData.startTime && bookingData.endTime) {
      const start = new Date(`2000-01-01T${bookingData.startTime}`);
      const end = new Date(`2000-01-01T${bookingData.endTime}`);
      const diffMs = end - start;
      const diffHours = diffMs / (1000 * 60 * 60);
      setBookingData(prev => ({ ...prev, duration: diffHours }));
    }
  };

  useEffect(() => {
    calculateDuration();
  }, [bookingData.startTime, bookingData.endTime]);

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center">
          <FaMicrophone className="mr-3 text-purple-600" />
          Book Podcast Studio Session
        </h1>
        <p className="text-gray-600">Submit your podcast recording session request</p>
      </div>

      {/* Simple Booking Form */}
      <div className="bg-white rounded-lg shadow-lg p-8">
        <form onSubmit={handleBookingSubmit} className="space-y-6">
          {/* Section 1: Basic Booking Details */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FaCalendarAlt className="mr-2 text-purple-600" />
              Booking Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
                <input
                  type="date"
                  name="date"
                  value={bookingData.date}
                  onChange={(e) => setBookingData(prev => ({ ...prev, date: e.target.value }))}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Time *</label>
                  <input
                    type="time"
                    name="startTime"
                    value={bookingData.startTime}
                    onChange={(e) => setBookingData(prev => ({ ...prev, startTime: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Time *</label>
                  <input
                    type="time"
                    name="endTime"
                    value={bookingData.endTime}
                    onChange={(e) => setBookingData(prev => ({ ...prev, endTime: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            </div>

            {bookingData.duration > 0 && (
              <div className="mt-4 bg-white p-4 rounded-lg border border-purple-200">
                <p className="text-sm text-gray-700">
                  Duration: <span className="font-semibold">{bookingData.duration} hours</span>
                </p>
              </div>
            )}
          </div>

          {/* Section 2: Podcast Information */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FaMicrophone className="mr-2 text-purple-600" />
              Podcast Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Podcast Topic *</label>
                <input
                  type="text"
                  name="topic"
                  value={bookingData.topic}
                  onChange={(e) => setBookingData(prev => ({ ...prev, topic: e.target.value }))}
                  placeholder="e.g., Technology Trends, Health & Wellness, Business Interviews"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Podcast Title *</label>
                <input
                  type="text"
                  name="podcastTitle"
                  value={bookingData.podcastTitle}
                  onChange={(e) => setBookingData(prev => ({ ...prev, podcastTitle: e.target.value }))}
                  placeholder="e.g., The Future of AI"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Podcast Description</label>
                <textarea
                  name="podcastDescription"
                  value={bookingData.podcastDescription}
                  onChange={(e) => setBookingData(prev => ({ ...prev, podcastDescription: e.target.value }))}
                  rows="3"
                  placeholder="Brief description of your podcast episode..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Guest Information */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FaUser className="mr-2 text-purple-600" />
              Guest Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Guest Name</label>
                <input
                  type="text"
                  name="guestName"
                  value={bookingData.guestName}
                  onChange={(e) => setBookingData(prev => ({ ...prev, guestName: e.target.value }))}
                  placeholder="Guest's full name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Guest Email</label>
                <input
                  type="email"
                  name="guestEmail"
                  value={bookingData.guestEmail}
                  onChange={(e) => setBookingData(prev => ({ ...prev, guestEmail: e.target.value }))}
                  placeholder="guest@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Guest Phone</label>
                <input
                  type="tel"
                  name="guestPhone"
                  value={bookingData.guestPhone}
                  onChange={(e) => setBookingData(prev => ({ ...prev, guestPhone: e.target.value }))}
                  placeholder="+91 98765 43210"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Section 4: Contact Information */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
              <FaBuilding className="mr-2 text-purple-600" />
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person *</label>
                <input
                  type="text"
                  name="contactPerson"
                  value={bookingData.contactPerson}
                  onChange={(e) => setBookingData(prev => ({ ...prev, contactPerson: e.target.value }))}
                  placeholder="Your full name"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                <input
                  type="text"
                  name="companyName"
                  value={bookingData.companyName}
                  onChange={(e) => setBookingData(prev => ({ ...prev, companyName: e.target.value }))}
                  placeholder="Your company or organization"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone *</label>
                <input
                  type="tel"
                  name="contactPhone"
                  value={bookingData.contactPhone}
                  onChange={(e) => setBookingData(prev => ({ ...prev, contactPhone: e.target.value }))}
                  placeholder="+91 98765 43210"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email *</label>
                <input
                  type="email"
                  name="contactEmail"
                  value={bookingData.contactEmail}
                  onChange={(e) => setBookingData(prev => ({ ...prev, contactEmail: e.target.value }))}
                  placeholder="your@email.com"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
                <textarea
                  name="notes"
                  value={bookingData.notes}
                  onChange={(e) => setBookingData(prev => ({ ...prev, notes: e.target.value }))}
                  rows="3"
                  placeholder="Any additional information or special requests..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center pt-6">
            <button
              type="submit"
              disabled={isLoading}
              className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-8 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-purple-800 transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <FaCheckCircle className="mr-2" />
                  Submit Booking Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PodcastBook;